const CART_QUANTITY_DEBOUNCE_MS = 300;

function formatCartMoney(cents, format) {
  const value = Number(cents);
  if (!Number.isFinite(value)) {
    return '$0.00';
  }

  if (window.Shopify?.formatMoney) {
    return window.Shopify.formatMoney(value, format);
  }

  return `$${(value / 100).toFixed(2)}`;
}

function getCartTotal(cart) {
  return Number(cart.total_price || 0);
}

function getShippingThreshold() {
  const message = document.querySelector('[data-cart-shipping-message]');
  const page = document.querySelector('.cart-page');
  const raw =
    message?.getAttribute('data-free-shipping-threshold') ||
    page?.getAttribute('data-free-shipping-threshold');
  const threshold = Number.parseInt(raw, 10);

  return Number.isFinite(threshold) ? threshold : 0;
}

function getCartPageConfig() {
  const page = document.querySelector('.cart-page');
  if (!page) return null;

  return {
    moneyFormat: page.dataset.moneyFormat || '${{amount}}',
    moneyWithCurrencyFormat:
      page.dataset.moneyWithCurrencyFormat || page.dataset.moneyFormat || '${{amount}}',
  };
}

function getReferenceLinePrice(item, compareAtUnit) {
  let referenceLinePrice = item.original_line_price;

  if (compareAtUnit) {
    const compareAtLinePrice = compareAtUnit * item.quantity;
    if (compareAtLinePrice > referenceLinePrice) {
      referenceLinePrice = compareAtLinePrice;
    }
  }

  return referenceLinePrice;
}

function updateLineItemPrice(article, item) {
  const priceContainer = article.querySelector('.cart-page__item-price');
  if (!priceContainer) return;

  const config = getCartPageConfig();
  const moneyFormat = config?.moneyFormat || '${{amount}}';
  const compareAtUnit = Number.parseInt(article.dataset.compareAtPrice, 10) || 0;
  const referenceLinePrice = getReferenceLinePrice(item, compareAtUnit);

  const priceSpan = priceContainer.querySelector('span.cart-page__item-price');
  if (priceSpan) {
    priceSpan.textContent = formatCartMoney(item.final_line_price, moneyFormat);
  }

  const existingStrike = priceContainer.querySelector('s');
  const existingDiscount = priceContainer.querySelector('.cart-page__item-discount');

  if (referenceLinePrice > item.final_line_price) {
    const saving = referenceLinePrice - item.final_line_price;
    const discountPercent = Math.round((saving * 100) / referenceLinePrice);

    if (existingStrike) {
      existingStrike.textContent = formatCartMoney(referenceLinePrice, moneyFormat);
    } else {
      const strike = document.createElement('s');
      strike.textContent = formatCartMoney(referenceLinePrice, moneyFormat);
      priceContainer.appendChild(strike);
    }

    if (existingDiscount) {
      existingDiscount.textContent = `${discountPercent}% Off`;
    } else {
      const discount = document.createElement('span');
      discount.className = 'cart-page__item-discount';
      discount.textContent = `${discountPercent}% Off`;
      priceContainer.appendChild(discount);
    }
  } else {
    existingStrike?.remove();
    existingDiscount?.remove();
  }
}

function updateShippingMessage(cart) {
  const message = document.querySelector('[data-cart-shipping-message]');
  if (!message) return;

  const threshold = getShippingThreshold();
  if (threshold <= 0) return;

  const config = getCartPageConfig();
  const moneyFormat = config?.moneyFormat || '${{amount}}';
  const total = getCartTotal(cart);
  const text = message.querySelector('[data-cart-shipping-text]');
  const link = message.querySelector('[data-cart-shipping-link]');
  if (!text || !link) return;

  message.hidden = false;

  if (total < threshold) {
    const remaining = threshold - total;
    text.textContent = `You’re ${formatCartMoney(remaining, moneyFormat)} away from free delivery`;
    link.hidden = false;
  } else {
    text.textContent = 'You qualify for free delivery!';
    link.hidden = true;
  }
}

function updateCartSummaryDelivery(cart) {
  const delivery = document.querySelector('[data-cart-summary-delivery]');
  if (!delivery) return;

  const threshold = getShippingThreshold();
  const total = getCartTotal(cart);
  const qualifiesForFreeDelivery = threshold > 0 && total >= threshold;

  delivery.hidden = !qualifiesForFreeDelivery;

  if (qualifiesForFreeDelivery) {
    const deliveryTotal = delivery.querySelector('strong');
    if (deliveryTotal) {
      deliveryTotal.textContent = 'Free';
    }
  }
}

function updateCartSummary(cart) {
  const config = getCartPageConfig();
  const moneyFormat = config?.moneyFormat || '${{amount}}';
  const totalText = formatCartMoney(cart.total_price, config?.moneyWithCurrencyFormat || moneyFormat);

  document
    .querySelectorAll('[data-cart-summary-total] strong, [data-cart-summary-total-bar] strong')
    .forEach((element) => {
      element.textContent = totalText;
    });

  updateCartSummaryDelivery(cart);
  updateShippingMessage(cart);
}

const cartQuantityTimers = new Map();
let cartQuantityQueue = Promise.resolve();
let cartQuantityPending = 0;
let cartNeedsReload = false;

async function updateCartItemQuantity(input) {
  const line = input.dataset.index;
  const quantity = Number(input.value);
  const article = input.closest('.cart-page__item');
  const previousValue = input.getAttribute('value') || input.defaultValue;

  if (!Number.isInteger(quantity) || !input.checkValidity()) {
    input.reportValidity();
    input.value = previousValue;
    input.closest('quantity-input')?.validateQtyRules();
    return;
  }

  const root = window.Shopify?.routes?.root || '/';
  const sectionId = document.querySelector('.cart-page')?.dataset.sectionId;
  article?.classList.add('is-updating-quantity');

  try {
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        line,
        quantity,
        sections: sectionId ? [sectionId] : [],
        sections_url: `${root}cart`,
      }),
    });

    const cart = await response.json();
    if (!response.ok || cart.status || cart.errors) {
      throw new Error(cart.description || cart.message || 'Unable to update quantity.');
    }

    if (quantity === 0) {
      article?.remove();
      document.querySelectorAll('.cart-page__item').forEach((element, index) => {
        element.dataset.line = String(index + 1);
        element.querySelector('.quantity__input').dataset.index = String(index + 1);
        element.querySelector('[data-cart-item-remove]').dataset.line = String(index + 1);
      });
      cartNeedsReload = true;
      return;
    }

    const item = cart.items[Number.parseInt(line, 10) - 1];
    if (!item) {
      window.location.reload();
      return;
    }

    input.value = item.quantity;
    input.setAttribute('value', String(item.quantity));

    if (article) {
      updateLineItemPrice(article, item);
    }

    updateCartSummary(cart);

    // Shopify renders discount allocations and currency formatting after recalculation.
    const sectionHtml = cart.sections?.[sectionId];
    if (sectionHtml) {
      const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const discounts = document.querySelector('[data-cart-summary-discounts]');
      const updatedDiscounts = html.querySelector('[data-cart-summary-discounts]');
      if (discounts && updatedDiscounts) discounts.replaceChildren(...updatedDiscounts.childNodes);
      const total = html.querySelector('[data-cart-summary-total] strong');
      if (total) document.querySelectorAll('[data-cart-summary-total] strong, [data-cart-summary-total-bar] strong')
        .forEach((node) => { node.textContent = total.textContent; });
      document.querySelectorAll('.cart-page__item').forEach((element, index) => {
        if (cart.items[index]) updateLineItemPrice(element, cart.items[index]);
      });
    } else {
      cartNeedsReload = true;
    }

    if (typeof updateNelkoCartCount === 'function') {
      updateNelkoCartCount();
    }
  } catch (error) {
    input.value = previousValue;
    console.error(error);
  } finally {
    article?.classList.remove('is-updating-quantity');
    input.closest('quantity-input')?.validateQtyRules();
  }
}

function setCartItemRemoveLoading(button, isLoading) {
  const spinner = button.querySelector('.loading__spinner');
  button.classList.toggle('loading', isLoading);
  button.toggleAttribute('aria-disabled', isLoading);
  button.disabled = isLoading;
  spinner?.classList.toggle('hidden', !isLoading);
}

document.addEventListener('click', async (event) => {
  const removeButton = event.target.closest('[data-cart-item-remove]');
  if (removeButton) {
    if (removeButton.getAttribute('aria-disabled') === 'true') return;

    event.preventDefault();
    setCartItemRemoveLoading(removeButton, true);

    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          line: removeButton.dataset.line,
          quantity: 0,
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status || data.errors) {
        throw new Error(data.description || data.message || 'Unable to remove item.');
      }

      if (typeof updateNelkoCartCount === 'function') {
        updateNelkoCartCount();
      }

      window.location.reload();
      return;
    } catch (error) {
      setCartItemRemoveLoading(removeButton, false);
      console.error(error);
    }
  }

  const button = event.target.closest('[data-coupon-apply]');
  if (!button) return;

  const container = button.closest('[data-coupon-form]');
  const input = container?.querySelector('#CartCoupon');
  const code = input?.value.trim();

  if (!code) {
    input?.focus();
    return;
  }

  const error = container.querySelector('[data-coupon-error]');
  button.disabled = true;
  if (error) error.hidden = true;
  const root = window.Shopify?.routes?.root || '/';
  try {
    const currentResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    const currentCart = currentResponse.ok ? await currentResponse.json() : { discount_codes: [] };
    const existingCodes = (currentCart.discount_codes || []).map((item) => typeof item === 'string' ? item : item.code).filter(Boolean);
    const codes = [...new Set([...existingCodes, code])];
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    if (!response.ok) throw new Error('Unable to apply this coupon.');
    const cart = await response.json();
    const discount = cart.discount_codes?.find((item) => item.code.toLowerCase() === code.toLowerCase());
    if (discount && discount.applicable === false) throw new Error('This coupon is not valid for your cart.');
    if (cart.discount_applications?.length || cart.cart_level_discount_applications?.length || cart.discount_codes?.length) {
      window.location.reload();
    } else {
      const returnPath = `${root}cart`;
      window.location.assign(`${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(returnPath)}`);
    }
  } catch (exception) {
    if (error) { error.textContent = exception.message; error.hidden = false; }
    button.disabled = false;
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' || event.target.id !== 'CartCoupon') return;
  event.preventDefault();
  event.target.closest('[data-coupon-form]')?.querySelector('[data-coupon-apply]')?.click();
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('.cart-page .quantity__input');
  if (!input) return;

  clearTimeout(cartQuantityTimers.get(input));
  cartQuantityTimers.set(input, setTimeout(() => {
    cartQuantityTimers.delete(input);
    cartQuantityPending += 1;
    cartQuantityQueue = cartQuantityQueue.then(() => updateCartItemQuantity(input)).finally(() => {
      cartQuantityPending -= 1;
      if (cartNeedsReload && cartQuantityPending === 0 && cartQuantityTimers.size === 0) {
        window.location.reload();
      }
    });
  }, CART_QUANTITY_DEBOUNCE_MS));
});

function initCartSummaryTotalBar() {
  const total = document.querySelector('[data-cart-summary-total]');
  const bar = document.querySelector('[data-cart-summary-total-bar]');
  const cartPage = document.querySelector('.cart-page');
  if (!total || !bar || !cartPage) return;

  const mobileQuery = window.matchMedia('(max-width: 767px)');
  let observer;
  const spacer = document.createElement('div');
  spacer.setAttribute('aria-hidden', 'true');
  document.body.append(spacer);

  function syncBarHeight() {
    spacer.style.height = `${bar.hidden ? 0 : bar.offsetHeight}px`;
  }

  function setBarVisible(isVisible) {
    bar.hidden = !isVisible;
    syncBarHeight();
  }

  function setupObserver() {
    observer?.disconnect();

    if (!mobileQuery.matches) {
      setBarVisible(false);
      return;
    }

    observer = new IntersectionObserver(([entry]) => {
      setBarVisible(!entry.isIntersecting);
    }, {
      threshold: 0,
    });

    observer.observe(total);
  }

  setupObserver();
  new ResizeObserver(syncBarHeight).observe(bar);

  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', setupObserver);
  } else {
    mobileQuery.addListener(setupObserver);
  }

  window.addEventListener('resize', () => {
    if (!bar.hidden) syncBarHeight();
  });
}

document.addEventListener('DOMContentLoaded', initCartSummaryTotalBar);
